import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * @typedef {Object} DragHighlight
 * @property {boolean} active
 * @property {string} fromColumnId
 * @property {Set<string>} allowedTargetIds
 */

/**
 * @typedef {Object} CardMoving
 * @property { { id: string } } current Kanban card id being dragged
 */

/**
 * @typedef {Object} DragHighlightPackage
 * @property {CardMoving} cardMoving
 * @property {DragHighlight} dragHighlight
 * @property {Function} setDragHighlight
 * @property {Function} clearDragHighlight
 * @property {Function} attachDragEndListeners
 */

/**
 * @returns {DragHighlightPackage} package - Drag highlight state and methods
 */
export default function useDragHighlight() {
  const [dragHighlight, setDragHighlight] = useState({
    active: false,
    fromColumnId: null,
    allowedTargetIds: new Set(),
  })
  const listenerCleanupsRef = useRef([])

  const cardMoving = useRef({})

  const detachDragEndListeners = useCallback(() => {
    listenerCleanupsRef.current.forEach(remove => remove())
    listenerCleanupsRef.current = []
  }, [])

  const clearDragHighlight = useCallback(() => {
    setDragHighlight(prev =>
      prev.active ? { active: false, fromColumnId: null, allowedTargetIds: new Set() } : prev
    )
    cardMoving.current = {}
    detachDragEndListeners()
  }, [detachDragEndListeners])

  const attachDragEndListeners = useCallback(() => {
    const once = { once: true }
    const onKeyUp = e => e.key === 'Escape' && clearDragHighlight()
    const onVisibility = () => document.hidden && clearDragHighlight()
    const listeners = [
      { target: window, type: 'pointerup', handler: clearDragHighlight, options: once },
      { target: window, type: 'mouseup', handler: clearDragHighlight, options: once },
      { target: window, type: 'touchend', handler: clearDragHighlight, options: once },
      { target: window, type: 'pointercancel', handler: clearDragHighlight, options: once },
      { target: window, type: 'dragend', handler: clearDragHighlight, options: once },
      { target: window, type: 'keyup', handler: onKeyUp },
      { target: document, type: 'visibilitychange', handler: onVisibility, options: once },
    ]
    listeners.forEach(({ target, type, handler, options }) => {
      target.addEventListener(type, handler, options)
    })

    listenerCleanupsRef.current = listeners.map(({ target, type, handler, options }) => () => {
      target.removeEventListener(type, handler, options)
    })
  }, [clearDragHighlight])

  useEffect(() => {
    return () => detachDragEndListeners()
  }, [detachDragEndListeners])

  return {
    cardMoving,
    dragHighlight,
    setDragHighlight,
    clearDragHighlight,
    attachDragEndListeners,
  }
}
